import React, { memo, useMemo } from "react";
import { FiChevronUp } from "react-icons/fi";
import { isMobileOnly } from "react-device-detect";
import ScrollToTop from "./common/ScrollToTop";
import { imgSize } from "../utils/funcs";
import { BackgroundImageEntity, SponsorEntity, AboutEntity } from "../types/strapi";
import PageHelmet from "./common/Helmet";
import Header from "./common/Header";
import Breadcrumb from "./common/Breadcrumb";
import AboutComp from "./about/About";
import Testimonial from "./Testimonial";
import Image from "./objects/Image";
import BrandTwo from "./BrandTwo";
import FooterTwo from "./common/FooterTwo";
import TextBlock from "./objects/TextBlock";
import NewsSection from "./objects/NewsSection";
import { sanitizeAbout } from "../utils/aboutUtils";
import type { News } from "../types/strapi";

interface AboutProps {
  about?: Record<string, unknown> | AboutEntity;
  backgroundImages?: BackgroundImageEntity[];
  sponsors: SponsorEntity;
}

const About = ({
  about = {},
  backgroundImages = [],
  sponsors,
}: AboutProps) => {
  const section = "About";

  const memoizedAbout = useMemo(() => sanitizeAbout(about as Record<string, unknown>), [about]);
  const memoizedBackgroundImg = useMemo(
    () =>
      backgroundImages.find(
        (image) =>
          image?.section.toLowerCase() === section.toLowerCase()
      ),
    [backgroundImages]
  );

  const backgroundAttachmentType = isMobileOnly ? "scroll" : "fixed";

  const getBackgroundImageStyle = () => {
    const image = memoizedBackgroundImg?.image;
    return {
      backgroundImage: `url(${imgSize(image ?? undefined, isMobileOnly)})`,
      backgroundAttachment: backgroundAttachmentType,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
    };
  };

  return (
    <>
      <PageHelmet pageTitle={section} />
      <Header />
      <Breadcrumb backgroundSection={section} title={section} />

      <div className="about-area ptb--100 bg_color--1">
        <AboutComp
          shHeading={memoizedAbout.superHeadingOne}
          heading={memoizedAbout.headingOne}
          textBlock={memoizedAbout.textOne}
        />
      </div>

      <div
        className={`rn-finding-us-area attachment-${backgroundAttachmentType} rn-finding-us ptb--120 bg_color--1 bg_image`}
        data-black-overlay="5"
        style={getBackgroundImageStyle()}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div className="inner">
                <div className="content-wrapper">
                  <div className="content">
                    <h3 className="theme-gradient">
                      {memoizedAbout.headingTwo}
                    </h3>
                    <TextBlock textBlock={memoizedAbout.textTwo} />
                    <a
                      className="btn-default size-md"
                      href="/cases/cultural-ecologies-of-memory-in-transitional-colombia#chapters"
                    >
                      Methodology
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NewsSection news={(about as { news?: News[] })?.news} />

      <Testimonial sponsors={sponsors} />

      {backgroundImages.length !== 0 && (
        <Image
          caption={
            memoizedBackgroundImg?.image?.caption || ""
          }
          cssClass="mb-dec--40"
          type="fullWidth"
          imgUrl={imgSize(
            memoizedBackgroundImg?.image ?? undefined,
            isMobileOnly
          )}
        />
      )}

      <BrandTwo sponsors={sponsors} />

      <div className="backto-top">
        <ScrollToTop showUnder={160}>
          <FiChevronUp />
        </ScrollToTop>
      </div>

      <FooterTwo />
    </>
  );
};

export default memo(About);
